// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { useGenericQuery } from '@Queries/genericQueries';
import React, { ReactElement, useEffect, useState } from 'react';
import { OrganisationOutput, OrganisationLinksOutput, SharingProfileOutput } from 'thehive-sdk';
import { OrganisationEventList } from '@Organisms/Organisation/eventList';
import { useListAllFields } from '@Queries/describeQueries';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import useValidateForm from '@Hooks/useValidateForm';
import { Divider } from 'antd';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import { ICONS } from '@Constants/iconsApp';
import { THInput } from '@Molecules/Inputs/THInput/THInput';

import './OrganisationLinkManage.scss';

interface OrganisationLinksFormValues {
    links: {
        selected: boolean;
        linkType: string;
        otherLinkType: string;
        avatar: string;
        name: string;
    }[];
}

interface OrganisationLinksSelectorProps {
    orgList: OrganisationOutput[];
    sharingProfileList: SharingProfileOutput[];
    currentOrganisation?: OrganisationOutput;
}

interface ExplanationSelectParams {
    name: string;
    sharingProfile?: SharingProfileOutput;
    currentOrganisation?: string;
    otherOrganisation?: string;
}

const ExplanationSelect = ({
    name,
    sharingProfile,
    currentOrganisation = '',
    otherOrganisation = '',
}: ExplanationSelectParams): ReactElement => {
    const local = useLocalMessage();
    return (
        <p className="link-type__value">
            <span>{name}</span>
            <span className="link-type__value-explanation">
                {sharingProfile?.autoShare
                    ? local('organisation.links.form.link-type.help.shared', {
                          currentOrganisation,
                          otherOrganisation,
                          profile: sharingProfile?.permissionProfile,
                      })
                    : local('organisation.links.form.link-type.help.not-shared', {
                          currentOrganisation,
                          otherOrganisation,
                      })}
            </span>
        </p>
    );
};

const OrganisationLinksSelector = ({
    orgList,
    sharingProfileList,
    currentOrganisation,
}: OrganisationLinksSelectorProps): ReactElement => {
    const i = useLocalMessage();
    const [filter, setFilter] = useState('');

    return (
        <THForm.List name="links">
            {() => {
                return (
                    <div>
                        <THInput
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            placeholder="Filter by organisation name"
                        />

                        {orgList.map(({ name, _id, avatar }, index) => {
                            return name.toLowerCase().includes(filter.toLowerCase()) ? (
                                <div key={_id}>
                                    {index !== 0 && <Divider />}

                                    <div className="organisation-link-manage__name">
                                        <THForm.Item name={[index, 'selected']} valuePropName="checked">
                                            <THCheckbox />
                                        </THForm.Item>
                                        <THForm.Item hidden name={[index, 'name']}>
                                            <THInput value={name} hidden />
                                        </THForm.Item>
                                        <UserAvatar name={name} avatar={avatar} /> {name}
                                    </div>

                                    <div className="organisation-link-manage__link-display">
                                        <THForm.Item noStyle shouldUpdate>
                                            {({ getFieldValue, setFields }) => {
                                                const field = getFieldValue(['links', index]);

                                                return (
                                                    <THForm.Item
                                                        label={
                                                            <span className="organisation-link-manage__link-display__label">
                                                                {currentOrganisation?.name} <ICONS.ArrowRightOutlined />{' '}
                                                                {name}
                                                            </span>
                                                        }
                                                        name={[index, 'linkType']}
                                                        rules={[
                                                            {
                                                                required: field?.selected,
                                                                message: 'A link type is required!',
                                                            },
                                                        ]}
                                                    >
                                                        <THSelect
                                                            placeholder={i(
                                                                'organisation.links.form.link-type.placeholder',
                                                            )}
                                                            options={sharingProfileList.map((sharingProfile) => ({
                                                                label: (
                                                                    <ExplanationSelect
                                                                        name={sharingProfile.name}
                                                                        currentOrganisation={currentOrganisation?.name}
                                                                        otherOrganisation={name}
                                                                        sharingProfile={sharingProfile}
                                                                    />
                                                                ),
                                                                value: sharingProfile.name,
                                                            }))}
                                                            onSelect={() =>
                                                                setFields([
                                                                    {
                                                                        name: ['links', index, 'selected'],
                                                                        value: true,
                                                                    },
                                                                ])
                                                            }
                                                        />
                                                    </THForm.Item>
                                                );
                                            }}
                                        </THForm.Item>
                                    </div>

                                    <div className="organisation-link-manage__link-display">
                                        <THForm.Item noStyle shouldUpdate>
                                            {({ getFieldValue, setFields }) => {
                                                const field = getFieldValue(['links', index]);

                                                return (
                                                    <THForm.Item
                                                        label={
                                                            <span className="organisation-link-manage__link-display__label">
                                                                {currentOrganisation?.name} <ICONS.ArrowLeftOutlined />{' '}
                                                                {name}
                                                            </span>
                                                        }
                                                        name={[index, 'otherLinkType']}
                                                        rules={[
                                                            {
                                                                required: field?.selected,
                                                                message: 'A link type is required!',
                                                            },
                                                        ]}
                                                    >
                                                        <THSelect
                                                            placeholder={i(
                                                                'organisation.links.form.other-link-type.placeholder',
                                                            )}
                                                            options={sharingProfileList.map((sharingProfile) => ({
                                                                label: (
                                                                    <ExplanationSelect
                                                                        name={sharingProfile.name}
                                                                        currentOrganisation={name}
                                                                        otherOrganisation={currentOrganisation?.name}
                                                                        sharingProfile={sharingProfile}
                                                                    />
                                                                ),
                                                                value: sharingProfile.name,
                                                            }))}
                                                            onSelect={() =>
                                                                setFields([
                                                                    {
                                                                        name: ['links', index, 'selected'],
                                                                        value: true,
                                                                    },
                                                                ])
                                                            }
                                                        />
                                                    </THForm.Item>
                                                );
                                            }}
                                        </THForm.Item>
                                    </div>
                                </div>
                            ) : null;
                        })}
                    </div>
                );
            }}
        </THForm.List>
    );
};

interface OrganisationLinkManageProps {
    orgId: string;
    currentOrganisation?: OrganisationOutput;
    onListLinkableOrgs: OrganisationEventList['onListLinkableOrganisations'];
    onListSharingProfiles: OrganisationEventList['onListSharingProfiles'];
    onSetLinks: OrganisationEventList['onSetLinks'];
    triggerElem: ReactElement;
    onVisibleChange?: (visible: boolean) => void;
}

export const OrganisationLinkManage = ({
    currentOrganisation,
    onListLinkableOrgs,
    onListSharingProfiles,
    onSetLinks,
    orgId,
    triggerElem,
    onVisibleChange,
}: OrganisationLinkManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const { data: describeData } = useListAllFields();

    const { data: orgList = [], refetch: refetchOrgList } = useGenericQuery({
        fetchCallback: () =>
            onListLinkableOrgs([
                {
                    field: 'name',
                    operator: 'not',
                    values: [orgId, 'admin'],
                    type: 'string',
                    describeAttribute: describeData?.organisation.attributes.find(({ name }) => name === 'name') as any,
                },
            ]),
        key: ['get-organisation-links'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => onListSharingProfiles(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: true,
    });

    useEffect(() => {
        form.resetFields();
    }, [orgList, form]);

    return (
        <THDrawer
            title={i('organisation.links.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                if (visible) {
                    refetchOrgList();
                }
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <THForm<OrganisationLinksFormValues>
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        links: orgList.map(({ name }) => {
                            const org = currentOrganisation?.links?.find(
                                ({ toOrganisation }) => toOrganisation === name,
                            );

                            return {
                                name,
                                linkType: org?.linkType,
                                otherLinkType: org?.otherLinkType,
                                avatar: org?.avatar,
                                selected: !!org,
                            };
                        }),
                    }}
                    onFinish={({ links = [] }) => {
                        toggleDrawer();
                        setFieldsTouched(false);
                        onSetLinks(
                            links.reduce(
                                (acc, { name, linkType, otherLinkType, avatar, selected }) =>
                                    selected
                                        ? [...acc, { toOrganisation: name, linkType, otherLinkType, avatar }]
                                        : acc,
                                [] as OrganisationLinksOutput[],
                            ),
                            orgId,
                        ).finally(() => form.resetFields());
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    toggleDrawer();
                                }}
                                disabled={!isValid || !fieldsTouched}
                            />
                        }
                    >
                        <OrganisationLinksSelector
                            orgList={orgList}
                            sharingProfileList={sharingProfileList}
                            currentOrganisation={currentOrganisation}
                        />
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
